
import { useState } from 'react';
import {
    Box,
    Chip,
    Collapse,
    Divider,
    List,
    ListItemButton,
    Paper,
    Typography
} from '@mui/material';
import {
    ExpandMore,
    ExpandLess,
    CheckCircleOutline,
    ErrorOutline,
    History
} from '@mui/icons-material';

const formatDate = (value) => {
    if (!value) return '-';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return '-';

    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).format(date);
};

const getStatusColor = (status) => {
    switch (status) {
        case 1:
            return "#1976d2"; // Azul

        case 2:
            return "#1976d2"; // Azul

        case 3:
            return "#2e7d32"; // Verde

        case 4:
            return "#d32f2f"; // Vermelho

        case 5:
            return "#1976d2"; // Azul

        case 6:
            return "#d32f2f"; // Vermelho

        case 7:
            return "#1976d2"; // Azul

        case 8:
            return "#d32f2f"; // Azul

        default:
            return "#757575"; // Cinza
    }
}

const getStatusLabel = (status) => {
    switch (status) {
        case 1:
            return "Pendente";

        case 2:
            return "Processando";

        case 3:
            return "Processado";

        case 4:
            return "Falhou";

        case 5:
            return "Parcialmente processado";

        case 6:
            return "Erro";

        case 7:
            return "A ser deletado";

        case 8:
            return "Deletado";

        default:
            return "Desconhecido";
    }
}

const getStatusBackgroundColor = (status) => {
    switch (status) {
        case 1:
            return "#e3f2fd";

        case 2:
            return "#e3f2fd";

        case 5:
            return "#e3f2fd";

        case 3:
            return "#e8f5e9";

        case 4:
            return "#ffebee";

        case 6:
            return "#ffebee";

        default:
            return "#f5f5f5";
    }
}

const StatusChip = ({ status }) => (
    <Chip
        label={getStatusLabel(status)}
        size="small"
        sx={{
            mt: 1,
            bgcolor: getStatusBackgroundColor(status),
            color: getStatusColor(status),
            fontWeight: 600,
        }}
    />
);

export const LLMDocumentStatusHistoryTimeline = ({ history = [] }) => {
    const [expandedItems, setExpandedItems] = useState({});

    const toggleItem = (id) => {
        setExpandedItems((previous) => ({
            ...previous,
            [id]: !previous[id]
        }));
    };

    if (!history.length) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight={140}
                gap={1}
            >
                <History color="disabled" sx={{ fontSize: 32 }} />
                <Typography variant="body2" color="text.secondary">
                    Nenhuma alteração de status registrada.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                maxHeight: 320,
                overflowY: 'auto',
                overflowX: 'hidden',
                minHeight: 0,
                pr: 0.5
            }}
        >
            <List disablePadding>
                {history.map((entry, index) => {
                    const id = entry.id ?? index;
                    const isExpanded = Boolean(expandedItems[id]);
                    const hasErrorMessage = Boolean(
                        entry.processingerrormessage?.trim()
                    );

                    return (
                        <Box key={id}>
                            <Paper
                                variant="outlined"
                                elevation={0}
                                sx={{
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    borderColor: isExpanded
                                        ? 'primary.main'
                                        : 'divider',
                                    transition: 'border-color 0.2s',
                                    margin: 2
                                }}
                            >
                                <ListItemButton
                                    onClick={() => toggleItem(id)}
                                    aria-expanded={isExpanded}
                                    sx={{
                                        py: 1.5,
                                        px: 2,
                                        gap: 1.5,
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mt: 0.25,
                                            color: entry.processingerror
                                                ? 'error.main'
                                                : 'success.main'
                                        }}
                                    >
                                        {entry.processingerror ? (
                                            <ErrorOutline />
                                        ) : (
                                            <CheckCircleOutline />
                                        )}
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            flexWrap="wrap"
                                            gap={1}
                                            mb={1}
                                        >
                                            <StatusChip
                                                status={entry.previousstatus}
                                            />

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                →
                                            </Typography>

                                            <StatusChip
                                                status={entry.nextstatus}
                                            />

                                            <Chip
                                                label={
                                                    entry.processingerror
                                                        ? 'Com erro'
                                                        : 'Sem erro'
                                                }
                                                color={
                                                    entry.processingerror
                                                        ? 'error'
                                                        : 'success'
                                                }
                                                size="small"
                                            />
                                        </Box>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                        >
                                            {formatDate(entry.createdat)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ mt: 0.5 }}>
                                        {isExpanded ? (
                                            <ExpandLess />
                                        ) : (
                                            <ExpandMore />
                                        )}
                                    </Box>
                                </ListItemButton>

                                <Collapse
                                    in={isExpanded}
                                    timeout="auto"
                                    unmountOnExit
                                >
                                    <Divider />

                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1.5,
                                            pl: { xs: 2, sm: 6 }
                                        }}
                                    >
                                        {hasErrorMessage ? (
                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    fontWeight={600}
                                                    display="block"
                                                    mb={0.5}
                                                >
                                                    Mensagem de erro
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    color="error.main"
                                                    sx={{
                                                        whiteSpace: 'pre-wrap',
                                                        overflowWrap: 'anywhere'
                                                    }}
                                                >
                                                    {entry.processingerrormessage}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Nenhuma mensagem de erro
                                                registrada.
                                            </Typography>
                                        )}
                                    </Box>
                                </Collapse>
                            </Paper>

                            {index < history.length - 1 && (
                                <Box sx={{ height: 8 }} />
                            )}
                        </Box>
                    )
                })}
            </List>
        </Box>
    )
}